import type {
  DataPointSearchResult,
  GeneralInfoSearchSection,
  GeneralInfoTemplateId,
  ProfileTemplate,
} from '@/app/(client)/network/-types';
import { groupSearchIdsByTemplate } from './esg';

/** The order the General Info sidebar and content pane render the 3 templates. */
export const GENERAL_INFO_TEMPLATE_ORDER: GeneralInfoTemplateId[] = [
  'ABOUT',
  'OTHER_IDS',
  'OPERATIONAL_METRICS',
];

/**
 * Groups General Info search hits into one section per template that has
 * matching *visible* fields, in sidebar order.
 *
 * The pane used to render only the sidebar-selected template and filter the
 * hits against it, so matches living in either of the other two templates were
 * silently dropped — and a query matching none of the selected template's
 * fields left a blank pane (DELTA-2461, same bug as the ESG tab).
 */
export function buildGeneralInfoSearchSections(
  results: DataPointSearchResult[],
  templates: Record<GeneralInfoTemplateId, ProfileTemplate | undefined>,
  labels: Record<GeneralInfoTemplateId, string>,
): GeneralInfoSearchSection[] {
  const idsByTemplate = groupSearchIdsByTemplate(results);
  const sections: GeneralInfoSearchSection[] = [];

  for (const templateId of GENERAL_INFO_TEMPLATE_ORDER) {
    const template = templates[templateId];
    if (!template) continue;
    const matchedIds = idsByTemplate[templateId];
    if (!matchedIds) continue;
    // Hidden data points never render, so they must not make a section "match"
    // either — otherwise an all-hidden template would show an empty card.
    const searchIds = new Set(
      Object.entries(template.data)
        .filter(([key, dp]) => matchedIds.has(key) && dp.isVisible !== false)
        .map(([key]) => key),
    );
    if (searchIds.size === 0) continue;
    sections.push({
      templateId,
      templateName: labels[templateId],
      template,
      searchIds,
    });
  }

  return sections;
}

/**
 * Narrows the tab-wide `fieldErrors` map (keyed `<TEMPLATE_ID>.<field>`, since
 * the same field key can exist in more than one template) down to one
 * template's errors, keyed by bare field key the way `ProfileSection` wants.
 */
export function templateFieldErrors(
  fieldErrors: Record<string, string>,
  templateId: GeneralInfoTemplateId,
): Record<string, string> {
  const prefix = `${templateId}.`;
  return Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, message]) => [key.slice(prefix.length), message]),
  );
}

/**
 * Narrows the tab-wide `pendingFields` set (keyed `<TEMPLATE_ID>.<field>`,
 * same reason as `templateFieldErrors`) down to one template's bare field
 * keys, the way `ProfileSection` wants.
 */
export function templateFieldPending(
  pendingFields: Set<string>,
  templateId: GeneralInfoTemplateId,
): Set<string> {
  const prefix = `${templateId}.`;
  const result = new Set<string>();
  for (const key of pendingFields) {
    if (key.startsWith(prefix)) result.add(key.slice(prefix.length));
  }
  return result;
}
